import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { getMyOffers, getPartnerClaims, getPartnerDashboard, Offer, PartnerClaim, PartnerDashboard } from '../../api';
import { compact, EditLink, formatDateTime, NoticeBox, OfferRow, PageHead, StatusBadge, usePortal } from './shared';
import { useFeatures } from '../../features';

// Offers a partner should act on: hidden from customers for a reason they can fix or follow up.
const needsAttention = (o: Offer) => ['NeedsCategory', 'Pending', 'Rejected', 'Suspended'].includes(o.status) || !!o.hiddenReason;

// "Dashboard": headline numbers, the latest offers and, while offer claiming is off (Cheers2Night is
// discovery-only), the offers that need the partner's attention instead of recent claims.
const PartnerOverview: React.FC = () => {
  const { account, to } = usePortal();
  const features = useFeatures();
  const [stats, setStats] = useState<PartnerDashboard | null>(null);
  const [offers, setOffers] = useState<Offer[] | null>(null);
  const [claims, setClaims] = useState<PartnerClaim[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    getPartnerDashboard().then(setStats).catch(() => setFailed(true));
    getMyOffers().then(setOffers).catch(() => setFailed(true));
    if (features.offerClaims) getPartnerClaims().then(setClaims).catch(() => setFailed(true));
  }, [features.offerClaims]);

  const attention = (offers ?? []).filter(needsAttention);

  return (
    <>
      <PageHead title="Dashboard" subtitle="Manage your venue, offers and events.">
        <Link className="pp-btn pp-gold" to={to('/partner-account/offers/new')}><AddIcon />Add New Offer</Link>
      </PageHead>
      <NoticeBox notice={failed ? { severity: 'error', text: 'Some dashboard data could not be loaded. Please refresh.' } : null} />

      <div className="pp-stats">
        <div className="pp-stat"><b>{stats ? stats.activeOffers : '–'}</b><p>Active Offers</p></div>
        {features.offerClaims && <div className="pp-stat"><b>{stats ? compact(stats.totalClaims) : '–'}</b><p>Total Claims{stats && stats.claimsLast24h > 0 ? ` · ${stats.claimsLast24h} new today` : ''}</p></div>}
        {features.offerClaims && <div className="pp-stat"><b>{stats ? compact(stats.redeemed) : '–'}</b><p>Redeemed</p></div>}
        <div className="pp-stat"><b>{stats ? compact(stats.totalViews) : '–'}</b><p>Offer Views</p></div>
      </div>

      <div className="pp-grid2" style={{ alignItems: 'start' }}>
        <section>
          <h3 className="pp-section">Recent Offers</h3>
          {offers === null ? (
            <p className="pp-muted">Loading offers...</p>
          ) : offers.length === 0 ? (
            <div className="pp-panel pp-empty">
              <h3>No offers yet</h3>
              <p className="pp-muted">Create your first offer so Cheers2Night customers nearby can find it.</p>
              <Link className="pp-btn pp-gold" to={to('/partner-account/offers/new')}><AddIcon />Add New Offer</Link>
            </div>
          ) : (
            <div className="pp-list">
              {offers.slice(0, 3).map((offer) => (
                <OfferRow key={offer.id} offer={offer} venueName={account?.name} actions={<EditLink to={to(`/partner-account/offers/${offer.id}/edit`)} />} />
              ))}
              <Link className="pp-btn pp-outline" to={to('/partner-account/offers')}>View all offers</Link>
            </div>
          )}
        </section>
        {!features.offerClaims && (
          <section>
            <h3 className="pp-section">Needs Your Attention</h3>
            <div className="pp-panel">
              {offers === null ? (
                <p className="pp-muted" style={{ margin: 0 }}>Loading...</p>
              ) : attention.length === 0 ? (
                <p className="pp-muted" style={{ margin: 0 }}>All good: every published offer is visible to customers.</p>
              ) : (
                <div className="pp-table-wrap">
                  <table className="pp-table">
                    <tbody>
                      {attention.slice(0, 6).map((o) => (
                        <tr key={o.id}>
                          <td><Link to={to(`/partner-account/offers/${o.id}/edit`)}><b>{o.title}</b></Link><br /><span className="pp-muted">{o.hiddenReason ?? (o.status === 'NeedsCategory' ? 'Choose a category to show it again.' : o.moderationNote ?? '')}</span></td>
                          <td className="num"><StatusBadge status={o.status === 'NeedsCategory' ? 'Needs Category' : o.status === 'Pending' ? 'In Review' : o.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        )}
        {features.offerClaims && <section>
          <h3 className="pp-section">Recent Claims</h3>
          <div className="pp-panel">
            {claims === null ? (
              <p className="pp-muted" style={{ margin: 0 }}>Loading claims...</p>
            ) : claims.length === 0 ? (
              <p className="pp-muted" style={{ margin: 0 }}>No claims yet. They appear here as soon as customers claim your offers.</p>
            ) : (
              <div className="pp-table-wrap">
                <table className="pp-table">
                  <tbody>
                    {claims.slice(0, 6).map((c) => (
                      <tr key={c.id}>
                        <td><b>{c.offerTitle}</b><br /><span className="pp-muted">{c.mobile} · {formatDateTime(c.claimedAtUtc)}</span></td>
                        <td className="num"><StatusBadge status={c.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Link className="pp-btn pp-outline" style={{ marginTop: 12 }} to={to('/partner-account/claims')}>Bookings & Claims</Link>
          </div>
        </section>}
      </div>
    </>
  );
};

export default PartnerOverview;
